import PropTypes from 'prop-types';
import React, {Component} from 'react';
import classNames from 'classnames';
import CollapseBase from '../CollapseBase/CollapseBase';
import debounce from 'lodash.debounce';
import './MoreOrLess.less';

class MoreOrLess extends Component {
    static propTypes = {
        /** id tag for Component. Required for assistive devices (a11y) */
        id: PropTypes.string.isRequired,
        /** Flag to determine if the component is expanded or not on mount */
        expanded: PropTypes.bool,
        /** Total number of line rows (ems) to display when compact */
        lines: PropTypes.number,
        /** Expand button text; Can be a string, element, or component */
        moreText: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func]).isRequired,
        /** Compact button text; Can be a string, element, or component */
        lessText: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func]).isRequired,
        /** Optional event handler to trigger when the component expands or collapses.  Passes the isExpanded boolean as a parameter */
        onExpandedChange: PropTypes.func,
        /** The CSS line-height of the content, used to calculate how many rows to display. You will likely not need to adjust this. */
        lineHeight: PropTypes.number,
        /** The CSS padding-bottom of the MoreOrLess container; used to calculate expanded and collapsed height. You will likely not need to adjust this. */
        paddingBottom: PropTypes.number,
        /** Change position of more/less text. Options: top-left | top-right | bottom-left | bottom-right | inline | block */
        expanderPosition: PropTypes.oneOf(['top-left', 'top-right', 'bottom-left', 'bottom-right', 'inline', 'block'])
    }

    static defaultProps = {
        expanded: false,
        lines: 6,
        lineHeight: 24,
        paddingBottom: 50,
        moreText: 'More',
        lessText: 'Less',
        onExpandedChange: () => {},
        expanderPosition: 'block'
    }

    constructor(props) {
        super(props);

        this.debounceHandler = debounce(this.setToggleTextNeeded, 100);

        this.state = {
            isExpanded: this.props.expanded,
            isToggleTextNeeded: true
        };
    }

    componentDidMount() {
        this.componentDidMountTimer = setTimeout(() => {
            this.setToggleTextNeeded();
        }, 100);

        this.addListeners();
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.expanded !== nextProps.expanded) {
            this.setState({
                isExpanded: nextProps.expanded
            });
        }
    }

    componentWillUnmount() {
        if (this.componentDidMountTimer) {
            clearTimeout(this.componentDidMountTimer);
        }

        this.detachListeners();
    }

    setToggleTextNeeded = () => {
        let contentHeight = this.containerBase.container.scrollHeight;

        if (this.getCompactHeight() > contentHeight) {
            this.setState({
                isToggleTextNeeded: false
            });
        }

        if (this.getCompactHeight() < contentHeight) {
            this.setState({
                isToggleTextNeeded: true
            });
        }
    }

    getCompactHeight() {
        return this.containerBase.getCompactHeight();
    }

    handleClick = (e) => {
        e.preventDefault();
        const isExpanded = !this.state.isExpanded;

        this.setState({isExpanded});

        if (!isExpanded &&
            this.containerBase.container.getBoundingClientRect().top < 0) {
            this.containerBase.container.scrollIntoView({block: 'start', behavior: 'smooth'});
        }
        this.props.onExpandedChange(isExpanded);
    }

    addListeners() {
        window.addEventListener('resize', this.debounceHandler);
    }

    detachListeners() {
        this.debounceHandler.cancel();
        window.removeEventListener('resize', this.debounceHandler);
    }

    render() {
        const {id, moreText, lessText, lines, children, className, expanded, paddingBottom, lineHeight, onExpandedChange, expanderPosition, ...rest} = this.props; // eslint-disable-line no-unused-vars
        const {isExpanded, isToggleTextNeeded} = this.state;
        const toggleText = (isExpanded) ? lessText : moreText;

        const isTopLeft = expanderPosition === 'top-left';
        const isTopRight = expanderPosition === 'top-right';
        const isBottomLeft = expanderPosition === 'bottom-left';
        const isBottomRight = expanderPosition === 'bottom-right';
        const isInlineInside = expanderPosition === 'inline';
        const isInlineOutside = isTopLeft || isTopRight || isBottomLeft || isBottomRight;
        const isBlock = expanderPosition === 'block' || (!isInlineInside && !isInlineOutside);

        const divClassName = classNames(className, {
            'MoreOrLess': true,
            'MoreOrLess--block': isBlock && isToggleTextNeeded,
            'MoreOrLess--inline-inside': isInlineInside,
            'MoreOrLess--inline-outside': isInlineOutside
        });

        const toggleClassName = classNames('text-link', {
            'MoreOrLess__toggle': true,
            'MoreOrLess__toggle--block': isBlock,
            'MoreOrLess__toggle--block-expanded': isExpanded && isBlock,
            'MoreOrLess__toggle--top-left': isTopLeft,
            'MoreOrLess__toggle--top-right': isTopRight,
            'MoreOrLess__toggle--bottom-left': isBottomLeft,
            'MoreOrLess__toggle--bottom-right': isBottomRight,
            'MoreOrLess__toggle--inline-inside': isInlineInside,
            'MoreOrLess__toggle--inline-outside': isInlineOutside
        });

        const collapseBaseClassName = classNames({
            'MoreOrLess__CollapseBase--padding': isInlineInside
        });

        return (
            <div
                id={id}
                className={divClassName}
                {...rest}
                ref={
                    /* istanbul ignore next */
                    (c) => {
                        this.container = c;
                    }
                }
            >
                <CollapseBase
                    className={collapseBaseClassName}
                    expanded={isExpanded}
                    lines={lines}
                    lineHeight={lineHeight}
                    paddingBottom={paddingBottom}
                    ref={(base) => {
                        this.containerBase = base;
                    }}
                >
                    {children}
                </CollapseBase>
                {isToggleTextNeeded &&
                    <button
                        aria-expanded={isExpanded}
                        aria-controls={id}
                        onClick={this.handleClick}
                        className={toggleClassName}
                    >
                        <span className="MoreOrLess__toggle-text">{toggleText}</span>
                    </button>
                }
            </div>
        );
    }
}

export default MoreOrLess;
